import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Settings, Mail, CheckCircle, XCircle, Send, Eye, EyeOff, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [form, setForm] = useState({
    resend_api_key: '',
    sender_email: 'noreply@rss.sm.my',
    sender_name: 'Rumpun Silat Sarawak',
  });
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      setIsConfigured(data.is_configured);
      setForm(f => ({
        ...f,
        sender_email: data.sender_email || 'noreply@rss.sm.my',
        sender_name: data.sender_name || 'Rumpun Silat Sarawak',
        resend_api_key: data.is_configured ? '' : '',
      }));
    } catch {
      toast.error('Gagal memuatkan tetapan.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.resend_api_key.trim()) {
      toast.error('Sila masukkan API Key Resend.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/settings/email`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tetapan email berjaya disimpan!');
      setIsConfigured(true);
      setForm(f => ({ ...f, resend_api_key: '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan tetapan.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Sila masukkan alamat email penerima.');
      return;
    }
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/settings/email/test`, { recipient_email: testEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Email ujian telah dihantar ke ${testEmail}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghantar email ujian.');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="animate-pulse text-[#D71920] text-sm font-medium">Memuatkan tetapan...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#D71920]/10 flex items-center justify-center flex-shrink-0">
            <Settings className="w-5 h-5 text-[#D71920]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#0B0B0B]">Tetapan Sistem</h1>
            <p className="text-sm text-[#999]">Konfigurasi integrasi dan pemberitahuan</p>
          </div>
        </div>

        {/* Status Card */}
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${
          isConfigured
            ? 'bg-green-50 border-green-200'
            : 'bg-amber-50 border-amber-200'
        }`} data-testid="email-status-card">
          {isConfigured ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">Email Dikonfigurasi</p>
                <p className="text-xs text-green-600">Sistem akan menghantar email sebenar melalui Resend.</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Email Belum Dikonfigurasi</p>
                <p className="text-xs text-amber-600">Email hanya dilog ke konsol server. Tiada email sebenar dihantar.</p>
              </div>
            </>
          )}
        </div>

        {/* Email Configuration Form */}
        <div className="bg-white rounded-xl border border-[#E5E5E5]">
          <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#D71920]" />
            <h2 className="text-sm font-bold text-[#0B0B0B]">Konfigurasi Email (Resend)</h2>
          </div>
          <form onSubmit={handleSave} className="p-5 space-y-4">
            {/* Resend API Key */}
            <div>
              <label className="block text-xs font-semibold text-[#555] mb-1.5">
                API Key Resend <span className="text-[#D71920]">*</span>
              </label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={form.resend_api_key}
                  onChange={e => setForm(f => ({ ...f, resend_api_key: e.target.value }))}
                  placeholder={isConfigured ? 'Masukkan API key baru untuk kemaskini' : 're_xxxxxxxxxxxxxxxxxxxx'}
                  className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]/20"
                  data-testid="resend-api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#555]"
                >
                  {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <p className="text-xs text-[#999]">Dapatkan API key anda di</p>
                <a
                  href="https://resend.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#D71920] hover:underline flex items-center gap-0.5"
                >
                  resend.com/api-keys <ExternalLink size={10} />
                </a>
              </div>
            </div>

            {/* Sender Name */}
            <div>
              <label className="block text-xs font-semibold text-[#555] mb-1.5">
                Nama Pengirim
              </label>
              <input
                type="text"
                value={form.sender_name}
                onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))}
                placeholder="Rumpun Silat Sarawak"
                className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]/20"
                data-testid="sender-name-input"
              />
            </div>

            {/* Sender Email */}
            <div>
              <label className="block text-xs font-semibold text-[#555] mb-1.5">
                Alamat Email Pengirim
              </label>
              <input
                type="email"
                value={form.sender_email}
                onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))}
                placeholder="noreply@rss.sm.my"
                className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]/20"
                data-testid="sender-email-input"
              />
              <p className="text-xs text-[#999] mt-1">
                Domain ini mesti disahkan dalam akaun Resend anda.
              </p>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-[#D71920] hover:bg-[#B5161C] text-white text-sm"
              data-testid="save-email-settings-btn"
            >
              {saving ? 'Menyimpan...' : isConfigured ? 'Kemaskini Tetapan' : 'Simpan & Aktifkan'}
            </Button>
          </form>
        </div>

        {/* Test Email */}
        {isConfigured && (
          <div className="bg-white rounded-xl border border-[#E5E5E5]">
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
              <Send className="w-4 h-4 text-[#D71920]" />
              <h2 className="text-sm font-bold text-[#0B0B0B]">Hantar Email Ujian</h2>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-[#777]">
                Ujikan konfigurasi email anda dengan menghantar email percubaan.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  placeholder="email@contoh.com"
                  className="flex-1 border border-[#E5E5E5] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]/20"
                  data-testid="test-email-input"
                />
                <Button
                  type="button"
                  onClick={handleTestEmail}
                  disabled={testing}
                  className="bg-[#0B0B0B] hover:bg-[#333] text-white text-sm px-4"
                  data-testid="send-test-email-btn"
                >
                  {testing ? 'Menghantar...' : 'Hantar'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-[#F7F7F7] rounded-xl border border-[#E5E5E5] p-5">
          <h3 className="text-xs font-bold text-[#555] mb-3 uppercase tracking-wider">Email Yang Dihantar Oleh Sistem</h3>
          <ul className="space-y-2">
            {[
              'Kata laluan sementara apabila perguruan diluluskan',
              'Notifikasi penolakan permohonan perguruan',
              'Amaran penggantungan/pengarkiban perguruan',
              'Notifikasi permohonan ahli baru kepada admin perguruan',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-[#777]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D71920] mt-1.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
